import { NavLink } from 'react-router-dom';
import { useFilters } from '../context/FilterContext';
import { useApi } from '../hooks/useApi';

export default function Sidebar({ open = false, onClose }) {
  const { region, step, setRegion, setStep } = useFilters();
  const { data: regionsData } = useApi('/api/regions');
  const { data: stepsData } = useApi('/api/steps');

  const regions = regionsData?.regions || [];
  const steps = stepsData?.steps || [];

  const navItems = [
    { to: '/dashboard', icon: 'sports_soccer', label: 'Dashboard' },
    { to: '/leagues', icon: 'emoji_events', label: 'Leagues' },
    { to: '/players', icon: 'groups', label: 'Players' },
    { to: '/search', icon: 'search', label: 'Search' },
    { to: '/blog', icon: 'article', label: 'Blog' },
  ];

  return (
    <aside
      aria-label="Primary navigation"
      className={`fixed left-0 top-0 z-[60] flex h-screen w-64 flex-col overflow-y-auto border-r border-outline-variant/20 bg-surface-container-low py-6 font-headline antialiased tracking-tight transition-transform duration-200 ease-out ${
        open ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}
    >
      <div className="px-6 mb-8 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-expanded text-xl font-bold tracking-tight text-on-background">Below The Radar</h1>
          <p className="mt-0.5 text-xs text-on-surface-variant font-medium">The talent they're not tracking</p>
          <span className="mt-2 inline-flex items-center gap-1.5 font-label text-[10px] uppercase tracking-[0.12em] text-primary/80">
            <span className="material-symbols-outlined text-[12px]">sports_soccer</span>
            Non-League &bull; Steps 5-7
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          className="lg:hidden -mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface transition-colors"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <nav className="flex-1 space-y-1">
        {navItems.map(({ to, icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onClose}
            className={({ isActive }) =>
              `flex min-h-[44px] items-center gap-3 px-4 py-3 transition-colors duration-150 ${
                isActive
                  ? 'text-primary bg-primary/[0.08] border-l-2 border-primary'
                  : 'text-on-surface-variant hover:bg-surface-container-highest/50 hover:text-on-background border-l-2 border-transparent'
              }`
            }
          >
            <span className="material-symbols-outlined">{icon}</span>
            <span className="text-sm font-semibold">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-4 mt-8">
        <p className="px-4 font-label text-[10px] font-semibold text-on-surface-variant uppercase tracking-[0.12em] mb-4">
          Region & Step Filters
        </p>
        <div className="space-y-4 px-4">
          <div>
            <label className="font-label text-[10px] uppercase tracking-[0.12em] text-on-surface-variant/70 block mb-2">Region</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full bg-surface-container-lowest border border-outline-variant/20 text-xs rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/70 text-on-surface px-3 py-2.5"
            >
              <option value="">All Regions</option>
              {regions.map((r) => (
                <option key={r.id || r.name} value={r.name}>{r.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-label text-[10px] uppercase tracking-[0.12em] text-on-surface-variant/70 block mb-2">Step Level</label>
            <div className="grid grid-cols-3 gap-2">
              {steps.map((s) => {
                const stepNum = String(s.step_number);
                const isActive = step === stepNum;
                return (
                  <button
                    key={stepNum}
                    onClick={() => setStep(isActive ? '' : stepNum)}
                    className={`flex min-h-[40px] items-center justify-center text-xs font-bold rounded-lg transition-all duration-150 active:scale-95 ${
                      isActive
                        ? 'bg-primary text-on-primary'
                        : 'bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface'
                    }`}
                  >
                    {stepNum}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
